import UserManager from '../UserManager'
import { v4 as uuid } from 'uuid'
import { getNowUnixTimestamp } from '../../utils/dateUtils'
import { daysBetween } from '../../utils/DateTimeUtils'
import { logger } from '../logger'
import {
  analytics,
  EVENT_NAMES,
  FACEBOOK_INITIATE_CHECKOUT_EVENT_NAME,
  FACEBOOK_PURCHASE_EVENT_NAME,
} from './Analytics'
import { getCurrentUtmParams, getInitialUtmParams } from './utm'
import { generateCartAnalytics } from './cartAnalytics'
import { getEnrichedFacebookEventData } from './facebookAnalytics'
import { shippingOptionsManager } from '../ShippingOptionsManager'
import {
  calculateShippingRange,
  shouldShowDeliveryRange,
} from '../../stores/shippingStore/shipping.utils'
import { getProductPromisesProps } from './productArrivalsAnalytics'
import { shouldWarnTileLowQuality } from '../../pages/PhotoStyler/TileWarning/tileWarning.utils'
import { convertPriceToUSDCurrency } from '../../utils/utils'
import countBy from 'lodash/countBy'
import { CROP_TYPE } from '../../utils/cropUtils.consts'
import capitalize from 'lodash/capitalize'
import { amplitude } from './platforms/amplitude'
import { intercomService } from './platforms/intercom'
import {
  serverSideAnalytics,
  ServerSideAnalyticsPlatform,
} from './ServerSideAnalytics'
import { _analytics } from './AnalyticsImpl.js'
import { ORDER_ITEM_TYPE, TILE_CONTENT } from '@mixtiles/web-backend-shared'
import { getBrazeInstance } from './platforms/braze.client'
import { brazeWhitelistEvents } from './platforms/brazeWhitelistEvents'
import { brazeWhitelistAttributes } from './platforms/brazeWhitelistAttributes'
import { getAddressStore } from '../../stores/RootStoreProvider'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { promoCodeManager } from '../PromoCodeManager'
import StorageManager from '../StorageManager'
import { WHO_IS_IT_FOR_KEY } from '../../pages/EmailCapturePage/EmailCapturePage.consts'
import { getTilesSizesNames } from '../../components/PhotoBank/tileUtils'
import { getViewType } from './photoAnalytics/photoAnalytics'

const DAY = 1000 * 60 * 60 * 24

function _facebookTrackOrderCompleted({
  eventName,
  eventData,
  eventID,
  orderTrackingData,
  initialUserParams,
  orderItems,
  contentParams = {},
}) {
  analytics.facebookTrack(
    eventName,
    {
      ...eventData,
      ...getEnrichedFacebookEventData({
        orderTrackingData,
        initialUserParams,
        orderItems,
        value: orderTrackingData['Order Revenue'],
        contentParams,
      }),
    },
    eventID
  )
}

export function getViewContentParams({ tiles }) {
  const allArtworks = tiles
    .filter((tile) => tile.artwork)
    .map((tile) => tile.artwork)
  if (allArtworks.length > 0) {
    const contentName =
      allArtworks.length === 1 ? allArtworks[0].title : 'mix items'

    const contentCategory = 'Art'
    return {
      contentCategory,
      contentName,
    }
  }
}

export function trackOrderCompleted({
  orderTrackingData,
  initialUserParams,
  prefix,
  isFirstOrder,
  orderItems,
  emailAddedRecently,
  orderPriceSummary,
  contentParams,
}) {
  prefix = prefix ? `${prefix} ` : ''
  const orderCompletedEventName = `${prefix}Order Completed`
  const purchaseEventName = prefix + FACEBOOK_PURCHASE_EVENT_NAME
  const fbPurchaseEventName = prefix + FACEBOOK_PURCHASE_EVENT_NAME

  orderTrackingData = {
    ...orderTrackingData,
    'App Platform': _analytics.getAppPlatform(),
    isFirstOrder: isFirstOrder?.toString(),
    'Email Status': UserManager.isNewEmail() ? 'New Email' : 'Existed Before',
    'Email Added Recently': emailAddedRecently,
    ...getPromiseDateProps(),
    ...getMinimumOrderFeeProps(orderPriceSummary),
    ...getShippingPricingProps(orderPriceSummary),
  }

  const ANY_ORDER_COMPLETION_EVENT_NAME = 'Order Completed Combined' // captures tiles / gift card order

  try {
    amplitude.track(ANY_ORDER_COMPLETION_EVENT_NAME, orderTrackingData)

    amplitude.track(orderCompletedEventName, orderTrackingData)
    if (orderTrackingData['Art Collection Order']) {
      amplitude.track(EVENT_NAMES.PURCHASE_ART_COLLECTION, orderTrackingData)
    }
    if (orderTrackingData['Book Order']) {
      amplitude.track(EVENT_NAMES.PURCHASE_PHOTO_BOOK, orderTrackingData)
    }
    intercomService.track(orderCompletedEventName, orderTrackingData)

    const eventID = uuid()
    const lastOrderTimestamp = getNowUnixTimestamp()
    const braze = getBrazeInstance()

    // Get Braze user once for all operations
    const brazeUser = braze.getUser()
    if (!brazeUser) {
      logger.warning('Braze user not available for order analytics')
    }

    // Only send whitelisted attributes to Braze
    if (brazeWhitelistAttributes.includes('last_order_timestamp')) {
      brazeUser.setCustomUserAttribute(
        'last_order_timestamp',
        lastOrderTimestamp
      )
    }

    if (brazeWhitelistEvents.includes(orderCompletedEventName)) {
      if (orderCompletedEventName.includes('Order Completed')) {
        const orderQuantity = 1
        braze.logPurchase(
          orderPriceSummary?.productType || orderCompletedEventName, // productId
          orderTrackingData['USD Revenue'],
          'USD',
          orderQuantity,
          {
            ...orderTrackingData,
            $event_id: eventID,
          }
        )

        // Add to custom attribute array for Order Completed events
        const utmParams = getCurrentUtmParams()
        const initialUtmParams = getInitialUtmParams(initialUserParams)

        const orderData = {
          $add: [
            {
              'USD Revenue': orderTrackingData['USD Revenue'],
              'Book Order': orderTrackingData['Book Order'] || 'False',
              'Art Order': orderTrackingData['Art Order'] || 'False',
              'AI Templates Order':
                orderTrackingData['AI Templates Order'] || 'False',
              'Promo Code': orderTrackingData['Promo Code'] || null,
              'Order Tiles': orderTrackingData['Order Tiles'],
              'Photo Books Count': orderTrackingData['Photo Books Count'],
              'Product Types Ordered':
                orderTrackingData['Product Types Ordered'],
              utm_source:
                utmParams.utm_source || initialUtmParams.utm_source || null,
              time: new Date().toISOString(),
            },
          ],
        }

        // Only send whitelisted attributes to Braze
        if (brazeWhitelistAttributes.includes(orderCompletedEventName)) {
          brazeUser.setCustomUserAttribute(
            orderCompletedEventName,
            orderData,
            true
          )
        }
      } else {
        braze.logCustomEvent(orderCompletedEventName, {
          ...orderTrackingData,
          $event_id: eventID,
        })
      }
    }

    const eventData = {
      currency: orderTrackingData.Currency,
      value: orderTrackingData['Order Revenue'],
      isFirstOrder: orderTrackingData.isFirstOrder,
      'Email Status': orderTrackingData['Email Status'],
      'Order ID': orderTrackingData['Local ID'],
      'Order Group UID': orderTrackingData['Order Group UID'],
      'Order Items Count': orderTrackingData['Order Items Count'],
      'Photo Count': orderTrackingData['Photo Count'],
      'Photo Books Count': orderTrackingData['Photo Books Count'],
      'Non 8x8 Photo Wall': orderTrackingData['Non 8x8 Photo Wall'],
      'Non 8x8 Single Tiles': orderTrackingData['Non 8x8 Single Tiles'],
      'Non 8x8 Order': orderTrackingData['Non 8x8 Order'],
      'Photos Order': orderTrackingData['Photos Order'],
      'Book Order': orderTrackingData['Book Order'],
      'Art Collection Order': orderTrackingData['Art Collection Order'],
      'ArtPhoto USD Revenue': orderTrackingData['ArtPhoto USD Revenue'],
      'ArtPhoto Count': orderTrackingData['ArtPhoto Count'],
      'Email Added Recently': emailAddedRecently,
      'Hash Email': orderTrackingData['Hash Email'],
      'Is Mixed Products': orderTrackingData['Is Mixed Products'],
      'Product Types Ordered': orderTrackingData['Product Types Ordered'],
      'Has Promo Code': orderTrackingData['Has Promo Code'],
      'Discount Progress State': orderTrackingData['Discount Progress State'],
      'Cart Items Count': orderTrackingData['Cart Items Count'],
    }

    // Add all AI Templates fields dynamically - both high level analytics like "AI Templates Order" and category-based analytics like "AI Templates Family USD Revenue"
    if (orderTrackingData['AI Templates Order']) {
      Object.keys(orderTrackingData).forEach((key) => {
        if (key.startsWith('AI Templates ')) {
          eventData[key] = orderTrackingData[key]
        }
      })
    }
    let facebookEventData = { ...eventData }

    _facebookTrackOrderCompleted({
      eventName: fbPurchaseEventName,
      eventData: facebookEventData,
      eventID,
      orderTrackingData,
      initialUserParams,
      orderItems,
      contentParams,
    })
    serverSideAnalytics.track(orderCompletedEventName, eventData, eventID, [
      ServerSideAnalyticsPlatform.Braze,
    ])
    serverSideAnalytics.track(
      purchaseEventName,
      {
        properties: {
          currency: eventData.currency,
          value: eventData.value,
        },
      },
      eventID,
      [ServerSideAnalyticsPlatform.Tiktok]
    )

    // Setting new email false so next order the email will not be new
    UserManager.setIsNewEmail(false)

    window.dataLayer.push({
      event: purchaseEventName,
      eventID,
      conversionValue: orderTrackingData['Order Revenue'],
      value: orderTrackingData['Order Revenue'],
      currencyCode: orderTrackingData.Currency,
      transaction_id: orderTrackingData['Local ID'],
      transactionId: orderTrackingData['Local ID'],
      quantity: orderTrackingData['Order Tiles'],
      ...getCurrentUtmParams(),
      ...getInitialUtmParams(initialUserParams),
      ...eventData,
    })

    window.dataLayer.push({
      event: 'ecomm_event',
      transactionId: orderTrackingData['Local ID'],
      transactionTotal: orderTrackingData['Order Revenue'],
      currency: orderTrackingData.Currency,
      // 'transactionTax': '0',
      // 'transactionShipping': '0',
      transactionProducts: [
        {
          sku: 'PT_8x8',
          name: 'Photo Tile (8x8)',
          price:
            orderTrackingData['Order Revenue'] /
            orderTrackingData['Order Tiles'],
          currency: orderTrackingData.Currency,
          quantity: orderTrackingData['Order Tiles'],
        },
      ],
    })
  } catch (error) {
    logger.error(error)
  }
}

// ugly workaround since I don't to change the actual productType - because it will change functionality and might take more time for tests
// Also will require some better design (like Provider that wraps and inject event properties properly)
export function getProductTypeForEvent(productType, orderItem) {
  return orderItem?.itemType === ORDER_ITEM_TYPE.PHOTO_WALL
    ? ORDER_ITEM_TYPE.PHOTO_WALL
    : productType
}

export function trackCheckoutSheetOpened({
  orderTiles,
  orderItems,
  orderPriceSummary,
  includePricingSummaryFields,
  productType,
  bagProps,
  entryPoint,
}) {
  const promiseDateProps = getPromiseDateProps()
  const lowQualityTiles = orderItems[0]?.orderTiles?.filter((tile) =>
    shouldWarnTileLowQuality(tile)
  )
  const artTiles = orderItems[0]?.orderTiles?.filter(
    (tile) => tile.tileContent === TILE_CONTENT.ART_PHOTO
  )

  analytics.trackWithOneTimeServerSide(
    EVENT_NAMES.CHECKOUT_SHEET_OPENED,
    {
      ...generateCartAnalytics(
        orderItems,
        orderPriceSummary,
        includePricingSummaryFields
      ),
      ...getViewType(productType),
      ...bagProps,
      ...promiseDateProps,
      ...getMinimumOrderFeeProps(orderPriceSummary),
      ...getShippingPricingProps(orderPriceSummary),
      'Low-Resolution Tiles Count': lowQualityTiles?.length,
      'Product Type': getProductTypeForEvent(productType, orderItems[0]),
      'Art Tiles Count': artTiles?.length,
      'Checkout Entry Point': entryPoint,
    },
    [ServerSideAnalyticsPlatform.Braze, ServerSideAnalyticsPlatform.Tiktok]
  )
  UserManager.getInitialUserParams().then((initialUserParams) => {
    analytics.facebookTrack(FACEBOOK_INITIATE_CHECKOUT_EVENT_NAME, {
      ...getEnrichedFacebookEventData({
        orderTrackingData: {
          'Promo Code': promoCodeManager.getDiscountCouponDisplayName(),
          'Order Tiles': orderTiles.length,
          'Tile Sizes': getTilesSizesNames(orderTiles),
          'Who Is It For': StorageManager.get(WHO_IS_IT_FOR_KEY) || null,
        },
        initialUserParams,
        orderItems,
        value: orderPriceSummary?.priceBeforeTax,
        currency: orderPriceSummary?.currency,
        contentParams: getViewContentParams({ tiles: orderTiles }),
      }),
      ...promiseDateProps,
    })
  })
}

export function getPromiseDateProps() {
  if (!readOutsideReaction(() => getAddressStore().currentAddress)) {
    return {}
  }

  const { shippingOptions } = shippingOptionsManager.getStoredShippingOptions()
  if (!shippingOptions?.length) {
    return {}
  }

  return getShippingOptionsPromiseDateProps(shippingOptions)
}

export function getShippingOptionsPromiseDateProps(shippingOptions) {
  const props = getProductPromisesProps(shippingOptions)
  shippingOptions.forEach((shippingOption) => {
    const { shippingOptionName, promisedArrivalDate } = shippingOption
    const promisedDate = new Date(promisedArrivalDate.iso)
    const today = new Date()
    props[`Promise Date: ${shippingOptionName}`] = promisedDate
    props[`Promise Date Gap: ${shippingOptionName}`] = Math.ceil(
      (promisedDate - today) / DAY
    )

    if (shouldShowDeliveryRange(shippingOption)) {
      const { startDateISO, endDateISO } =
        calculateShippingRange(shippingOption)
      props[`Promised Arrival Date: ${shippingOptionName} - Lower Bound`] =
        startDateISO
      props[`Promised Arrival Date: ${shippingOptionName} - Days Range`] =
        daysBetween(startDateISO, endDateISO) + 1
    }
  })
  return props
}

export function getMinimumOrderFeeProps(priceSummary) {
  if (!priceSummary || Object.keys(priceSummary).length === 0) {
    return null
  } else {
    const { orderFeePrice, currency } = priceSummary
    return {
      'Minimum Order Fee': orderFeePrice
        ? convertPriceToUSDCurrency(orderFeePrice ?? 0, currency)
        : 0,
    }
  }
}

export function getShippingPricingProps(priceSummary) {
  if (!priceSummary || Object.keys(priceSummary).length === 0) {
    return null
  } else {
    const { shippingCost, currency, shippingBuyOverData } = priceSummary
    return {
      'Shipping Price': shippingCost
        ? convertPriceToUSDCurrency(shippingCost, currency)
        : 0,
      'Minimum Shipping Threshold': shippingBuyOverData?.buyOverPrice
        ? convertPriceToUSDCurrency(shippingBuyOverData.buyOverPrice, currency)
        : 0,
    }
  }
}

export function getCropsDistribution(orderUploads) {
  if (!orderUploads) {
    return {}
  }
  const distribution = countBy(orderUploads, (u) => {
    const params = u.cropRect || u.cropParams
    return params?.cropType
  })
  // Add missing crop types as 0
  Object.values(CROP_TYPE).forEach((cropType) => {
    if (!distribution[cropType]) {
      distribution[cropType] = 0
    }
  })

  const cropFitCount = orderUploads.filter(
    (orderUpload) =>
      orderUpload.cropParams?.height > orderUpload.height &&
      orderUpload.cropParams?.width > orderUpload.width
  ).length
  const cropFitDistribution = {
    'Crop Fit Photos Count': cropFitCount,
    'Crop Fit Photos Percentage': Math.round(
      (100 * cropFitCount) / orderUploads.length
    ),
  }

  return {
    ...Object.entries(distribution)
      .map(([cropType, count]) => ({
        [`${capitalize(cropType)} Cropped Photos Count`]: count,
        [`${capitalize(cropType)} Cropped Photos Percentage`]: Math.round(
          (100 * count) / orderUploads.length
        ),
      }))
      .reduce((acc, obj) => ({ ...acc, ...obj }), {}),
    ...cropFitDistribution,
  }
}

export function getSmartCropAdjustmentMetrics(orderUploads) {
  if (!orderUploads) return {}

  const totalPhotos = orderUploads.length
  const smartCroppedPhotos = orderUploads.filter((u) => {
    const params = u.cropRect || u.cropParams
    return params?.wasSmartCropped
  })
  const adjustedAfterSmartCrop = smartCroppedPhotos.filter((u) => {
    const params = u.cropRect || u.cropParams
    return params?.cropType === CROP_TYPE.MANUAL
  })

  const smartCroppedCount = smartCroppedPhotos.length
  const adjustedCount = adjustedAfterSmartCrop.length

  return {
    'Smart Cropped Photos Count': smartCroppedCount,
    'Smart Cropped Photos Percentage': totalPhotos
      ? Math.round((100 * smartCroppedCount) / totalPhotos)
      : 0,
    'Smart Crop Adjusted Count': adjustedCount,
    'Smart Crop Adjusted Percentage': smartCroppedCount
      ? Math.round((100 * adjustedCount) / smartCroppedCount)
      : 0,
  }
}
